import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, History, AlertTriangle, CheckCircle2, RotateCcw, FileText, Database } from "lucide-react";
import Layout from "../components/Layout";
import { formatDate } from "../lib/propiedadUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const descargarBlob = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const TIPO_LABEL = { exportacion: "Exportación", plantilla: "Plantilla", importacion: "Importación", restauracion: "Restauración" };
const TIPO_COLOR = {
  exportacion: "bg-indigo-100 text-indigo-700",
  plantilla: "bg-gray-100 text-gray-600",
  importacion: "bg-emerald-100 text-emerald-700",
  restauracion: "bg-amber-100 text-amber-700",
};

export default function MaestroCSV({ user, onLogout }) {
  const [validacion, setValidacion] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const [validando, setValidando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [historial, setHistorial] = useState([]);
  const fileRef = useRef(null);

  const cargarHistorial = () => {
    axios.get(`${API}/maestro/historial`, { headers: authHeader() })
      .then((r) => setHistorial(r.data))
      .catch(() => toast.error("Error al cargar la bitácora"));
  };

  useEffect(() => { cargarHistorial(); }, []);

  const descargar = async (ruta, nombre) => {
    try {
      const r = await axios.get(`${API}${ruta}`, { headers: authHeader(), responseType: "blob" });
      descargarBlob(r.data, nombre);
      cargarHistorial();
    } catch {
      toast.error("Error al descargar el archivo");
    }
  };

  const validar = async () => {
    if (!archivo) return toast.error("Selecciona un archivo CSV");
    setValidando(true);
    setValidacion(null);
    try {
      const fd = new FormData();
      fd.append("file", archivo);
      const r = await axios.post(`${API}/maestro/importar-validar`, fd, { headers: authHeader() });
      setValidacion(r.data);
      if (r.data.errores.length) toast.error(`Se encontraron ${r.data.errores.length} errores. Corrígelos antes de importar`);
      else toast.success("Archivo validado correctamente");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al validar el archivo");
    } finally {
      setValidando(false);
    }
  };

  const confirmar = async () => {
    setConfirmando(true);
    try {
      const r = await axios.post(`${API}/maestro/importar-confirmar`, { sesion_id: validacion.sesion_id }, { headers: authHeader() });
      toast.success(`Importación ejecutada: ${r.data.creados} creados, ${r.data.modificados} modificados, ${r.data.archivados} archivados`);
      setShowConfirm(false);
      setValidacion(null);
      setArchivo(null);
      if (fileRef.current) fileRef.current.value = "";
      cargarHistorial();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ejecutar la importación");
    } finally {
      setConfirmando(false);
    }
  };

  const restaurar = async (respaldoId) => {
    if (!window.confirm("¿Restaurar la información a como estaba ANTES de esta importación? Los registros creados en ella se eliminarán.")) return;
    try {
      const r = await axios.post(`${API}/maestro/respaldos/${respaldoId}/restaurar`, {}, { headers: authHeader() });
      toast.success(`Restaurado: ${r.data.propiedades_restauradas} propiedades y ${r.data.contratos_restaurados} contratos revertidos`);
      cargarHistorial();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al restaurar");
    }
  };

  const res = validacion?.resumen;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="maestro-csv-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-7 h-7 text-gray-700" /> Base maestra CSV
          </h1>
          <p className="text-sm text-gray-500 mt-1">Exporta, corrige masivamente e importa la información completa de Rotco. Exclusivo del usuario maestro.</p>
        </div>

        <Tabs defaultValue="exportar">
          <TabsList>
            <TabsTrigger value="exportar" data-testid="tab-exportar">Exportar</TabsTrigger>
            <TabsTrigger value="importar" data-testid="tab-importar">Importar</TabsTrigger>
            <TabsTrigger value="historial" data-testid="tab-historial" onClick={cargarHistorial}>Historial y respaldos</TabsTrigger>
          </TabsList>

          <TabsContent value="exportar" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Base maestra completa</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-500">Una fila por propiedad con su contrato más reciente, datos del propietario, inquilino, fiador y cobranza. Incluye los IDs internos para reimportar.</p>
                  <Button onClick={() => descargar("/maestro/exportar-csv", `base_maestra_rotco.csv`)} data-testid="descargar-base-btn">
                    <Download className="w-4 h-4 mr-1" /> Descargar base maestra CSV
                  </Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-600" /> Plantilla oficial</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-500">Encabezados oficiales, descripción de cada columna, tipos de dato, catálogos válidos, ejemplos y reglas (vacío = conservar, BORRAR = eliminar dato).</p>
                  <Button variant="outline" onClick={() => descargar("/maestro/plantilla-csv", "plantilla_maestra_rotco.csv")} data-testid="descargar-plantilla-btn">
                    <Download className="w-4 h-4 mr-1" /> Descargar plantilla maestra
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="importar" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Upload className="w-4 h-4 text-gray-600" /> Subir y actualizar base maestra</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-500">Carga el CSV con la estructura oficial. Primero se valida y se muestra una vista previa; nada se modifica hasta que confirmes. Antes de aplicar se crea un respaldo automático.</p>
                <div className="flex flex-col sm:flex-row gap-3 items-start">
                  <input ref={fileRef} type="file" accept=".csv" onChange={(e) => { setArchivo(e.target.files[0]); setValidacion(null); }}
                    className="text-sm file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-gray-900 file:text-white file:text-sm file:cursor-pointer border border-gray-200 rounded-lg w-full sm:w-auto"
                    data-testid="csv-file-input" />
                  <Button onClick={validar} disabled={validando || !archivo} data-testid="validar-csv-btn">
                    {validando ? "Validando..." : "Validar archivo"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {validacion && (
              <div className="space-y-4" data-testid="vista-previa">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    ["Filas leídas", res.total_filas, "text-gray-900"],
                    ["Nuevos", res.nuevos, "text-emerald-700"],
                    ["Actualizados", res.actualizados, "text-indigo-700"],
                    ["Sin cambios", res.sin_cambios, "text-gray-500"],
                    ["Con errores", res.con_errores, "text-red-600"],
                    ["Archivados", res.archivados, "text-amber-600"],
                  ].map(([l, v, c]) => (
                    <Card key={l}><CardContent className="pt-4 pb-3 text-center">
                      <p className={`text-2xl font-bold ${c}`}>{v}</p>
                      <p className="text-xs text-gray-500">{l}</p>
                    </CardContent></Card>
                  ))}
                </div>

                {validacion.advertencias?.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="pt-4 text-sm text-amber-800">
                      {validacion.advertencias.map((a, i) => <p key={i} className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {a}</p>)}
                    </CardContent>
                  </Card>
                )}

                {validacion.errores.length > 0 && (
                  <Card className="border-red-200">
                    <CardHeader className="pb-2"><CardTitle className="text-base text-red-700">Errores detectados ({validacion.errores.length}) — la importación está bloqueada</CardTitle></CardHeader>
                    <CardContent className="p-0 overflow-x-auto max-h-80 overflow-y-auto">
                      <table className="w-full text-sm" data-testid="tabla-errores">
                        <thead><tr className="border-b text-left text-xs uppercase text-gray-500">
                          <th className="px-4 py-2">Fila</th><th className="px-4 py-2">Columna</th><th className="px-4 py-2">Valor</th><th className="px-4 py-2">Motivo</th><th className="px-4 py-2">Acción recomendada</th>
                        </tr></thead>
                        <tbody>
                          {validacion.errores.map((e, i) => (
                            <tr key={i} className="border-b border-gray-100">
                              <td className="px-4 py-2 font-medium">{e.fila}</td>
                              <td className="px-4 py-2 text-red-600">{e.columna}</td>
                              <td className="px-4 py-2 text-gray-600 max-w-[140px] truncate">{e.valor || "—"}</td>
                              <td className="px-4 py-2">{e.motivo}</td>
                              <td className="px-4 py-2 text-gray-500">{e.accion}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                )}

                {validacion.total_cambios > 0 && (
                  <Card>
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                      <CardTitle className="text-base">Cambios detectados ({validacion.total_cambios} campos)</CardTitle>
                      <Button variant="outline" size="sm" onClick={() => descargar(`/maestro/sesiones/${validacion.sesion_id}/reporte-cambios`, "reporte_cambios.csv")} data-testid="descargar-reporte-btn">
                        <Download className="w-3.5 h-3.5 mr-1" /> Reporte comparativo
                      </Button>
                    </CardHeader>
                    <CardContent className="p-0 overflow-x-auto max-h-80 overflow-y-auto">
                      <table className="w-full text-sm" data-testid="tabla-cambios">
                        <thead><tr className="border-b text-left text-xs uppercase text-gray-500">
                          <th className="px-4 py-2">Fila</th><th className="px-4 py-2">Registro</th><th className="px-4 py-2">Campo</th><th className="px-4 py-2">Valor actual</th><th className="px-4 py-2">Valor nuevo</th>
                        </tr></thead>
                        <tbody>
                          {validacion.cambios.map((c, i) => (
                            <tr key={i} className="border-b border-gray-100">
                              <td className="px-4 py-2">{c.fila}</td>
                              <td className="px-4 py-2 font-medium">{c.registro}</td>
                              <td className="px-4 py-2 text-gray-600">{c.campo}</td>
                              <td className="px-4 py-2 text-red-600 max-w-[180px] truncate">{c.valor_actual || "—"}</td>
                              <td className="px-4 py-2 text-emerald-700 max-w-[180px] truncate">{c.valor_nuevo || "(borrado)"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {validacion.total_cambios > validacion.cambios.length && (
                        <p className="text-xs text-gray-400 px-4 py-2">Mostrando {validacion.cambios.length} de {validacion.total_cambios}. Descarga el reporte comparativo para verlos todos.</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                <div className="flex justify-end">
                  <Button onClick={() => setShowConfirm(true)} disabled={validacion.errores.length > 0 || (res.nuevos + res.actualizados === 0)}
                    className="bg-emerald-700 hover:bg-emerald-800" data-testid="abrir-confirmar-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Confirmar importación y sobrescribir cambios
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="historial" className="mt-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4 text-gray-600" /> Bitácora de actividad</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="tabla-historial">
                  <thead><tr className="border-b text-left text-xs uppercase text-gray-500">
                    <th className="px-4 py-2">Fecha</th><th className="px-4 py-2">Operación</th><th className="px-4 py-2">Usuario</th><th className="px-4 py-2">Archivo</th><th className="px-4 py-2">Resultado</th><th className="px-4 py-2">IP</th><th className="px-4 py-2 text-right">Acciones</th>
                  </tr></thead>
                  <tbody>
                    {historial.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Sin operaciones registradas</td></tr>}
                    {historial.map((b) => (
                      <tr key={b.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(b.fecha)} {b.fecha?.slice(11, 16)}</td>
                        <td className="px-4 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${TIPO_COLOR[b.tipo] || "bg-gray-100"}`}>{TIPO_LABEL[b.tipo] || b.tipo}</span></td>
                        <td className="px-4 py-2.5">{b.usuario_nombre}</td>
                        <td className="px-4 py-2.5 text-gray-600 max-w-[180px] truncate">{b.archivo_nombre || "—"}</td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {b.tipo === "importacion" ? `${b.creados ?? 0} creados · ${b.modificados ?? 0} modif. · ${b.archivados ?? 0} arch.` : b.registros_procesados != null ? `${b.registros_procesados} registros` : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-gray-400 text-xs">{b.ip || "—"}</td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          {b.sesion_id && b.tiene_archivo && (
                            <Button variant="ghost" size="sm" title="Descargar archivo original" onClick={() => descargar(`/maestro/sesiones/${b.sesion_id}/archivo`, b.archivo_nombre || "importacion.csv")} data-testid={`descargar-original-${b.id}`}>
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {b.sesion_id && b.tipo === "importacion" && (
                            <Button variant="ghost" size="sm" title="Descargar reporte de cambios" onClick={() => descargar(`/maestro/sesiones/${b.sesion_id}/reporte-cambios`, "reporte_cambios.csv")}>
                              <FileText className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {b.tipo === "importacion" && b.respaldo_id && (
                            b.respaldo_restaurado ? (
                              <span className="text-xs text-amber-600 ml-1">Restaurado</span>
                            ) : (
                              <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700" title="Restaurar versión anterior" onClick={() => restaurar(b.respaldo_id)} data-testid={`restaurar-${b.id}`}>
                                <RotateCcw className="w-3.5 h-3.5" />
                              </Button>
                            )
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent data-testid="confirmar-importacion-modal">
          <DialogHeader>
            <DialogTitle>Confirmar importación</DialogTitle>
            <DialogDescription>
              Se creará un respaldo automático y después se aplicarán los cambios: {res?.nuevos} registros nuevos, {res?.actualizados} actualizados y {res?.archivados} archivados ({validacion?.total_cambios} campos modificados). Podrás restaurar la versión anterior desde el historial.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancelar</Button>
            <Button onClick={confirmar} disabled={confirmando} className="bg-emerald-700 hover:bg-emerald-800" data-testid="confirmar-importacion-btn">
              {confirmando ? "Aplicando..." : "Confirmar y sobrescribir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
